import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createCustomCategory,
  createCustomDynamicCategory,
  deleteCustomCategory,
  getCustomCategories,
  reorderCustomCategories,
  updateCustomCategoryHidden,
  updateCustomCategoryName,
  updateCustomDynamicCategory,
} from "../../api/inventory.js";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import { FaCircleXmark, FaFloppyDisk, FaPencil, FaTrash } from "react-icons/fa6";
import { FaGripLines } from "react-icons/fa6";
import { FaChevronUp, FaChevronDown } from "react-icons/fa6";
import CategoryProducts from "./categoryProducts.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import { addToast } from "../../store/app/action.js";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import Tooltip from "../../components/Tooltip.jsx";
import {
  Configure,
  Index,
  InstantSearch,
  useHits,
  useStats,
} from "react-instantsearch-hooks";
import config from '@/config';
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import Autocomplete from "../products/autocomplete.jsx";
import { RangeSlider } from "../../components/InstantSearch/RangeSlider.jsx";
import CustomRefinementList from "../../components/InstantSearch/RefinementList.jsx";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import { formatValue } from "../../utils/Utils.js";

const Categories = () => {
  const { selectedMerchant, selectedLocation } = useSelector(
    (state) => state.app
  );
  const dispatch = useDispatch();
  const [deleteError, setDeleteError] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [nameEditable, setNameEditable] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [expandedCategoryID, setExpandedCategoryID] = useState(null);
  const [createNewCategory, setCreateNewCategory] = useState(false);

  const [hiding, setHiding] = useState({});
  const [deletingCategory, setDeletingCategory] = useState(null);

  useEffect(() => {
    (async () => {
      const customCategories = await getCustomCategories();
      setCustomCategories(customCategories.categories);
    })();
  }, [selectedMerchant]);

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const newItems = Array.from(customCategories);
    const [removed] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, removed);
    for (let i = 0; i < newItems.length; i++) {
      newItems[i].ordinal = i;
    }
    const ordinals = {};
    newItems.forEach(({ _id, ordinal }) => (ordinals[_id] = ordinal));
    setCustomCategories(newItems);

    await reorderCustomCategories(ordinals);
  };

  const toggleHideCategory = async (categoryID) => {
    setHiding({ ...hiding, [categoryID]: true });
    await updateCustomCategoryHidden(
      categoryID,
      !customCategories.find((c) => c._id === categoryID).hidden
    );
    setCustomCategories(
      customCategories.map((c) =>
        c._id === categoryID ? { ...c, hidden: !c.hidden } : c
      )
    );
    setHiding({ ...hiding, [categoryID]: false });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        {/* Left: Title */}
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            Categories 
          </h1>
        </div>
      </div>
      <div className="mb-4">
        <PrimaryButton
          onClick={() => {
            setCreateNewCategory(true);
          }}
          disabled={nameEditable}
        >
          Create New Category
        </PrimaryButton>
      </div>

      <ModalBasic
        modalOpen={Boolean(deleteWarning)}
        setModalOpen={() => setDeleteWarning(null)}
        title="Are you sure?"
        width="md"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm">
            Are you sure you want to delete this category?
          </p>
          <div className="flex gap-2 w-full justify-end">
            <SecondaryButton
              onClick={() => {
                setDeleteWarning(null);
              }}
              loading={deletingCategory === deleteWarning}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              loading={deletingCategory === deleteWarning}
              onClick={async () => {
                setDeletingCategory(deleteWarning);
                await deleteCustomCategory(deleteWarning);
                setCustomCategories(
                  customCategories.filter((c) => c._id !== deleteWarning)
                );
                setDeletingCategory(null);
                setDeleteWarning(null);
                dispatch(
                  addToast({
                    type: "success",
                    message: "Category deleted successfully.",
                  })
                );
              }}
            >
              Delete
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>
      <ModalBasic
        modalOpen={deleteError}
        setModalOpen={setDeleteError}
        title="Error"
        width="md"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm">
            You cannot delete a category with products in it.
          </p>
          <p className="text-sm">
            You must first remove all products from this category by navigating
            to the{" "}
            <a href="/catalog/products" className={"underline cursor-pointer"}>
              products page
            </a>
            , then filter by this category and use bulk edit to remove this
            category.
          </p>
          <div className="flex gap-2 w-full justify-end">
            <SecondaryButton
              onClick={() => {
                setDeleteError(false);
              }}
            >
              OK
            </SecondaryButton>
          </div>
        </div>
      </ModalBasic>
      <CategoryModal
        openCreateNewCategory={createNewCategory}
        setOpenCreateNewCategory={setCreateNewCategory}
        customCategories={customCategories}
        setCustomCategories={setCustomCategories}
      />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {customCategories.map((item, index) => {
                const DropDownButton =
                  expandedCategoryID === item._id ? FaChevronUp : FaChevronDown;
                return (
                  <Draggable
                    key={item._id}
                    draggableId={item._id}
                    index={index}
                    isDragDisabled={
                      Boolean(nameEditable) || Boolean(expandedCategoryID)
                    }
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="items-center justify-between user-select-none p-4 bg-white dark:bg-theme-surface-accent text-black dark:text-white border border-gray-300 dark:border-slate-600 rounded shadow mb-2 flex flex-col"
                        style={{
                          ...provided.draggableProps.style,
                        }}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex gap-2 items-center">
                            <DropDownButton
                              className="text-2xl"
                              onClick={() => {
                                if (expandedCategoryID === item._id) {
                                  setExpandedCategoryID(null);
                                } else {
                                  setExpandedCategoryID(item._id);
                                }
                              }}
                            />

                            {nameEditable === item._id ? (
                              <input
                                className="form-input"
                                type="text"
                                value={editingName}
                                onChange={(e) => {
                                  setEditingName(e.target.value);
                                }}
                              />
                            ) : (
                              item.name
                            )}
                            {nameEditable === item._id && (
                              <>
                                <PrimaryButton
                                  onClick={async () => {
                                    await updateCustomCategoryName(
                                      item._id,
                                      editingName
                                    );

                                    // update local

                                    const newItems =
                                      Array.from(customCategories);
                                    const index = newItems.findIndex(
                                      (i) => i._id === item._id
                                    );
                                    newItems[index].name = editingName;
                                    setCustomCategories(newItems);
                                    setNameEditable(null);
                                    setEditingName(null);
                                  }}
                                >
                                  <FaFloppyDisk />
                                </PrimaryButton>
                                <DangerButton
                                  onClick={() => {
                                    setNameEditable(null);
                                  }}
                                >
                                  <FaCircleXmark />
                                </DangerButton>
                              </>
                            )}
                            {!nameEditable &&
                              (!expandedCategoryID ||
                                expandedCategoryID === item._id) && (
                                <>
                                  <SecondaryButton
                                    onClick={() => {
                                      setNameEditable(item._id);
                                      setEditingName(item.name);
                                    }}
                                  >
                                    <FaPencil />
                                  </SecondaryButton>
                                  <SecondaryButton
                                    disabled={hiding[item._id]}
                                    onClick={() => {
                                      toggleHideCategory(item._id);
                                    }}
                                    loading={hiding[item._id]}
                                  >
                                    {item.hidden ? (
                                      <FaRegEyeSlash className="text-slate-400" />
                                    ) : (
                                      <FaRegEye />
                                    )}
                                  </SecondaryButton>
                                  <SecondaryButton
                                    loading={deletingCategory === item._id}
                                    onClick={async () => {
                                      if (item.products.length) {
                                        setDeleteError(true);
                                        return;
                                      }
                                      setDeleteWarning(item._id);
                                    }}
                                  >
                                    <FaTrash />
                                  </SecondaryButton>
                                </>
                              )}
                          </div>
                          {!Boolean(expandedCategoryID) &&
                            !Boolean(nameEditable) && (
                              <FaGripLines className="text-2xl" />
                            )}
                        </div>

                        {/*horizontal bar*/}
                        {!expandedCategoryID ||
                          (expandedCategoryID === item._id && (
                            <div className="w-full h-[1px] bg-gray-300 dark:bg-slate-600 my-4" />
                          ))}
                        {expandedCategoryID === item._id &&
                          Boolean(item.products.length) && (
                            <CategoryProducts
                              categoryID={expandedCategoryID}
                              allCategories={customCategories}
                            />
                          )}
                        {expandedCategoryID === item._id && item.is_dynamic && (
                          <>
                            <DynamicCategory
                              search={item.search}
                              categoryId={item._id}
                              discard={() => {
                                setExpandedCategoryID(null);
                              }}
                              customCategories={customCategories}
                              setCustomCategories={setCustomCategories}
                            />
                          </>
                        )}
                        {expandedCategoryID === item._id &&
                          !item.is_dynamic &&
                          !Boolean(item.products.length) && (
                            <div className={"w-1/2 pt-8 text-center text-sm"}>
                              To add products to this category, navigate to the{" "}
                              <a
                                href="/catalog/products"
                                className={"underline cursor-pointer"}
                              >
                                products page
                              </a>{" "}
                              and select the products you would like to include
                              in the category, then click{" "}
                              <PrimaryButton size="xs">
                                <span className={"flex"}>
                                  Bulk Edit{" "}
                                  <svg
                                    className="w-8 fill-current"
                                    viewBox="0 0 32 16"
                                  >
                                    <circle cx="16" cy="8" r="2" />
                                    <circle cx="10" cy="8" r="2" />
                                    <circle cx="22" cy="8" r="2" />
                                  </svg>
                                </span>
                              </PrimaryButton>{" "}
                              and select{" "}
                              <SecondaryButton size="xs">
                                Assign Categories
                              </SecondaryButton>
                              . Learn more{" "}
                              <a
                                href="https://support.saucey.com/en/articles/9627765-custom-categories"
                                target="_blank"
                                className={"underline cursor-pointer"}
                              >
                                here
                              </a>
                              .
                            </div>
                          )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

const Preview = ({ storeId }) => {
  const { hits } = useHits();
  const { nbHits } = useStats();
  const headers = ["", "Product", "Brand", "Category", "Price"];

  const localHits = useMemo(() => {
    const seen = {};
    hits.forEach((hit) => {
      if (seen[hit.sku_id]) {
        seen[hit.sku_id].default_category += `, ${hit.default_category}`;
      } else {
        seen[hit.sku_id] = hit;
      }
    });
    return Object.values(seen);
  }, [hits]);

  return (
    <div className="w-full">
      <table className="table-auto w-full dark:text-slate-300">
        <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
          <tr>
            {headers.map((header, index) => (
              <th
                key={"header_" + index}
                className="first:pl-5 last:pr-5 py-3 whitespace-nowrap text-left"
              >
                {" "}
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
          {localHits.map((hit, index) => {
            return (
              <tr key={hit._id}>
                <td className="pl-5">
                  <img src={hit.image} alt={hit.title} className="w-8 h-8" />
                </td>
                <td>
                  {hit.title} {hit.subtitle}
                </td>
                <td>{hit.company}</td>
                <td>{hit.default_category}</td>
                <td className="pr-5 py-3 w-24">
                  <div className="text-left">
                    <div>
                      {hit[`${storeId}_discounted_price`] ? (
                        <div>
                          <span className="text-green-600 font-bold">
                            {formatValue(hit[`${storeId}_discounted_price`])}
                          </span>{" "}
                          <span className="line-through">
                            {formatValue(hit[`${storeId}_price`])}
                          </span>
                        </div>
                      ) : (
                        formatValue(hit[`${storeId}_price`])
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
          {nbHits > 15 && (
            <tr>
              <td colSpan={headers.length} className="text-center py-4">
                And {nbHits - 15} more...
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const CategoryModal = ({
  openCreateNewCategory,
  setOpenCreateNewCategory,
  customCategories,
  setCustomCategories,
}) => {
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryType, setNewCategoryType] = useState("static");

  const [searchState, setSearchState] = useState({});
  const [priceDisabled, setPriceDisabled] = useState(false);
  const [onlyDiscountedItems, setOnlyDiscountedItems] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!openCreateNewCategory) {
      setNewCategoryName("");
      setNewCategoryType("static");
    }
  }, [openCreateNewCategory]);

  const [creatingNewCategory, setCreatingNewCategory] = useState(false);

  return (
    <ModalBasic
      modalOpen={openCreateNewCategory}
      setModalOpen={setOpenCreateNewCategory}
      title="Create New Category"
      width={newCategoryType === "static" ? "md" : "full"}
    >
      {openCreateNewCategory && (
        <div className="flex gap-2">
          <div
            className={`flex flex-col gap-4 ${
              newCategoryType === "static" ? "w-full" : "w-full"
            }`}
          >
            {newCategoryType === "dynamic" && (
              <div className="flex gap-2 w-full justify-start">
                <SecondaryButton
                  onClick={() => {
                    setOpenCreateNewCategory(false);
                    setNewCategoryName("");
                  }}
                  loading={creatingNewCategory}
                >
                  Cancel
                </SecondaryButton>

                <PrimaryButton
                  loading={creatingNewCategory}
                  onClick={async () => {
                    try {
                      setCreatingNewCategory(true);
                      const search = {};
                      let newCategory;

                      if (newCategoryType === "dynamic") {
                        const index = Object.keys(searchState)[0];
                        if (onlyDiscountedItems) {
                          search["discounted_only"] = true;
                        }
                        if (!priceDisabled && searchState[index].range) {
                          const rangeIndex = Object.keys(
                            searchState[index].range
                          )[0];
                          const range = searchState[index].range[rangeIndex];
                          const rangeSplit = range.split(":");
                          const to = rangeSplit[1];
                          const from = rangeSplit[0];
                          search["from_price"] = parseInt(from);
                          search["to_price"] = parseInt(to);
                        }
                        if (searchState[index].query) {
                          search["query"] = searchState[index].query;
                        }
                        if (searchState[index].refinementList) {
                          search["refinement_list"] =
                            searchState[index].refinementList;
                        }
                        newCategory = await createCustomDynamicCategory(
                          newCategoryName,
                          search
                        );
                      } else {
                        newCategory = await createCustomCategory(
                          newCategoryName
                        );
                      }

                      const newCategories = [
                        newCategory.category,
                        ...customCategories,
                      ];

                      setCustomCategories(newCategories);
                      for (let i = 0; i < newCategories.length; i++) {
                        newCategories[i].ordinal = i;
                      }
                      const ordinals = {};
                      newCategories.forEach(
                        ({ _id, ordinal }) => (ordinals[_id] = ordinal)
                      );
                      await reorderCustomCategories(ordinals);
                      setOpenCreateNewCategory(false);
                      setNewCategoryName("");
                    } catch (e) {
                      let message = "An error occurred";
                      try {
                        const serverMessage = e.response?.data?.message;
                        if (serverMessage) {
                          message = serverMessage;
                        }
                      } catch {}
                      message += ", category was not created.";
                      dispatch(
                        addToast({
                          type: "error",
                          message: message,
                        })
                      );
                    } finally {
                      setCreatingNewCategory(false);
                    }
                  }}
                >
                  Create
                </PrimaryButton>
              </div>
            )}
            <label className="block text-sm font-medium" htmlFor="categoryType">
              Category Name
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Category Name"
              value={newCategoryName}
              onChange={(e) => {
                setNewCategoryName(e.target.value);
              }}
            />
            <label
              className="block text-sm font-medium flex items-center gap-2"
              htmlFor="categoryType"
            >
              Category Type
              <Tooltip position="right">
                <div className="w-64">
                  <div className="mb-2">
                    <span className="font-bold">Static: </span>You assign
                    products to this category once it's created.
                  </div>
                  <div>
                    <span className="font-bold">Dynamic: </span>You setup
                    filters and search parameters, the category will
                    automatically update based on these parameters.
                  </div>
                </div>
              </Tooltip>
            </label>
            <select
              value={newCategoryType}
              id={"categoryType"}
              className="form-select"
              onChange={(e) => {
                setNewCategoryType(e.target.value);
              }}
            >
              <option value="static">Static</option>
              <option value="dynamic">Dynamic</option>
            </select>
            {newCategoryType === "dynamic" && (
              <DynamicCategory
                propagateSearchState={setSearchState}
                propagatePriceDisabled={setPriceDisabled}
                propagateOnlyDiscountedItems={setOnlyDiscountedItems}
              />
            )}

            {newCategoryType !== "dynamic" && (
              <div className="flex gap-2 w-full justify-end">
                <SecondaryButton
                  onClick={() => {
                    setOpenCreateNewCategory(false);
                    setNewCategoryName("");
                  }}
                  loading={creatingNewCategory}
                >
                  Cancel
                </SecondaryButton>

                <PrimaryButton
                  loading={creatingNewCategory}
                  onClick={async () => {
                    try {
                      setCreatingNewCategory(true);
                      const search = {};
                      let newCategory;

                      if (newCategoryType === "dynamic") {
                        const index = Object.keys(searchState)[0];
                        if (onlyDiscountedItems) {
                          search["discounted_only"] = true;
                        }
                        if (!priceDisabled && searchState[index].range) {
                          const rangeIndex = Object.keys(
                            searchState[index].range
                          )[0];
                          const range = searchState[index].range[rangeIndex];
                          const rangeSplit = range.split(":");
                          const to = rangeSplit[1];
                          const from = rangeSplit[0];
                          search["from_price"] = parseInt(from);
                          search["to_price"] = parseInt(to);
                        }
                        if (searchState[index].query) {
                          search["query"] = searchState[index].query;
                        }
                        if (searchState[index].refinementList) {
                          search["refinement_list"] =
                            searchState[index].refinementList;
                        }
                        newCategory = await createCustomDynamicCategory(
                          newCategoryName,
                          search
                        );
                      } else {
                        newCategory = await createCustomCategory(
                          newCategoryName
                        );
                      }

                      const newCategories = [
                        newCategory.category,
                        ...customCategories,
                      ];

                      setCustomCategories(newCategories);
                      for (let i = 0; i < newCategories.length; i++) {
                        newCategories[i].ordinal = i;
                      }
                      const ordinals = {};
                      newCategories.forEach(
                        ({ _id, ordinal }) => (ordinals[_id] = ordinal)
                      );
                      await reorderCustomCategories(ordinals);
                      setOpenCreateNewCategory(false);
                      setNewCategoryName("");
                    } catch (e) {
                      let message = "An error occurred";
                      try {
                        const serverMessage = e.response?.data?.message;
                        if (serverMessage) {
                          message = serverMessage;
                        }
                      } catch {}
                      message += ", category was not created.";
                      dispatch(
                        addToast({
                          type: "error",
                          message: message,
                        })
                      );
                    } finally {
                      setCreatingNewCategory(false);
                    }
                  }}
                >
                  Create
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
      )}
    </ModalBasic>
  );
};

const DynamicCategory = ({
  search,
  propagateSearchState,
  propagatePriceDisabled,
  propagateOnlyDiscountedItems,
  discard,
  setCustomCategories,
  customCategories,
  categoryId,
}) => {
  const { selectedLocation, selectedMerchant } = useSelector(
    (state) => state.app
  );
  const dispatch = useDispatch();
  const [onlyDiscountedItems, setOnlyDiscountedItems] = useState(false);
  const [previewLocation, setPreviewLocation] = useState(null);
  const [priceDisabled, setPriceDisabled] = useState(false);
  const [searchState, setSearchState] = useState({});
  const [savingCategory, setSavingCategory] = useState(false);
  useEffect(() => {
    if (!previewLocation && selectedLocation) {
      setPreviewLocation(selectedLocation.store_id);
    }
  }, [selectedLocation, previewLocation]);
  useEffect(() => {
    propagateOnlyDiscountedItems?.(onlyDiscountedItems);
    propagatePriceDisabled?.(priceDisabled);
    propagateSearchState?.(searchState);
  }, [searchState, priceDisabled, onlyDiscountedItems]);
  const { initialUiState, initMinMax } = useMemo(() => {
    if (!previewLocation) return { initialUiState: {}, initMinMax: null };
    if (!search) return { initialUiState: {}, initMinMax: null };
    const indexName = `${config.environment}_${selectedMerchant?.easy_id}_products`;
    const initState = {
      [indexName]: {
        configure: {},
      },
    };
    let initMinMax = [];
    if (search.from_price || search.to_price) {
      initState[indexName].range = {};
      initState[indexName].range[`${previewLocation}_price`] = `${
        search.from_price || ""
      }:${search.to_price || ""}`;

      initMinMax = [search.from_price || 0, search.to_price || undefined];
    }
    if (search.query) {
      initState[indexName].query = search.query;
    }
    if (search.refinement_list) {
      initState[indexName].refinementList = search.refinement_list;
    }
    if (search.discounted_only) {
      setOnlyDiscountedItems(true);
    }

    return { initialUiState: initState, initMinMax: initMinMax };
  }, [search, previewLocation]);

  const [typesenseInstantsearchAdapter] = useState(
    new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.ts.key, // Be sure to use an API key that only allows search operations
        nodes: config.ts.nodes,
        nearestNode: config.ts.nearestNode,
        cacheSearchResultsForSeconds: 2 * 60, // Cache search results from server. Defaults to 2 minutes. Set to 0 to disable caching.
      },
      // The following parameters are directly passed to Typesense's search API endpoint.
      //  So you can pass any parameters supported by the search endpoint below.
      //  query_by is required.
      additionalSearchParameters: {
        sort_by: "ordinal:asc,title:asc",
        query_by: "title,subtitle,default_category,description,company",
        group_by: "sku_id",
        group_limit: 10,
      },
    })
  );
  const searchClient = useMemo(() => {
    if (!typesenseInstantsearchAdapter.searchClient) return null;
    return typesenseInstantsearchAdapter.searchClient;
  }, [typesenseInstantsearchAdapter]);

  if (search && !initialUiState) {
    return null;
  }
  return (
    <InstantSearch
      indexName={`${config.environment}_${selectedMerchant?.easy_id}_products`}
      searchClient={searchClient}
      initialUiState={initialUiState}
      onStateChange={(state) => {
        setSearchState(state.uiState);
        state.setUiState(state.uiState);
        return state;
      }}
    >
      <div className="flex gap-2 w-full">
        <div className="flex-[2]">
          {/*<Index*/}
          {/*  indexName={`${config.environment}_${selectedMerchant?.easy_id}_products`}*/}
          {/*  indexId="search"*/}
          {/*>*/}
          <Configure hitsPerPage={15} />
          {categoryId && (
            <div className="flex mb-2 gap-2">
              <SecondaryButton loading={savingCategory} onClick={discard}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                loading={savingCategory}
                onClick={async () => {
                  try {
                    setSavingCategory(true);
                    const searchParams = {};
                    const index = Object.keys(searchState)[0];
                    if (onlyDiscountedItems) {
                      searchParams["discounted_only"] = true;
                    }
                    if (!priceDisabled && searchState[index].range) {
                      const rangeIndex = Object.keys(
                        searchState[index].range
                      )[0];
                      const range = searchState[index].range[rangeIndex];
                      const rangeSplit = range.split(":");
                      const to = rangeSplit[1];
                      const from = rangeSplit[0];
                      searchParams["from_price"] = parseInt(from);
                      searchParams["to_price"] = parseInt(to);
                    }
                    if (searchState[index].query) {
                      searchParams["query"] = searchState[index].query;
                    }
                    if (searchState[index].refinementList) {
                      searchParams["refinement_list"] =
                        searchState[index].refinementList;
                    }
                    await updateCustomDynamicCategory(categoryId, searchParams);

                    const idx = customCategories.findIndex(
                      (c) => c._id === categoryId
                    );
                    customCategories[idx].search = searchParams;
                    setCustomCategories(customCategories);
                    dispatch(
                      addToast({
                        type: "success",
                        message: "Category updated successfully.",
                      })
                    );
                    discard();
                  } catch (e) {
                    let message = "An error occurred";
                    try {
                      const serverMessage = e.response?.data?.message;
                      if (serverMessage) {
                        message = serverMessage;
                      }
                    } catch {}
                    message += ", category was not updated.";
                    dispatch(
                      addToast({
                        type: "error",
                        message: message,
                      })
                    );
                  } finally {
                    setSavingCategory(false);
                  }
                }}
              >
                Save
              </PrimaryButton>
            </div>
          )}
          <div className="mb-4 border px-4 pt-2 pb-4 bg-slate-50 dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 rounded">
            <span className="text-slate-700 dark:text-slate-300">Search Term:</span>
            <Autocomplete />
          </div>
          <div className="mb-4 pb-4 border px-4 pt-2 pb-4 bg-slate-50 dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 rounded">
            <div className="mb-6">
              <ToggleSwitch
                onLabel="Price:"
                offLabel="Price:"
                checked={!priceDisabled}
                onChange={() => {
                  setPriceDisabled(!priceDisabled);
                }}
              />
            </div>
            <div className="pb-12">
              <RangeSlider
                attribute={`${selectedLocation.store_id}_price`}
                disabled={priceDisabled}
                initMinMax={initMinMax}
              />
            </div>

            <ToggleSwitch
              onLabel="Only discounted items"
              offLabel="Only discounted items"
              checked={onlyDiscountedItems}
              onChange={() => {
                setOnlyDiscountedItems(!onlyDiscountedItems);
              }}
            />
          </div>
          {/*</Index>*/}

          <div className="flex gap-6">
            <div className="mb-4 pb-12 border px-4 pt-2 pb-4 bg-slate-50 dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 rounded w-1/2">
              <CustomRefinementList
                attribute="default_category"
                placeholder="Search Category"
              />
            </div>
            <div className="mb-4 pb-12 border px-4 pt-2 pb-4 bg-slate-50 dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 rounded w-1/2">
              <CustomRefinementList
                attribute="company"
                placeholder="Search Brand"
                showMore={true}
                showMoreLimit={500}
              />
            </div>
          </div>
        </div>

        <div className="flex-[3]">
          <h2 className="text-lg font-bold">
            <div className="flex items-center gap-2 mb-2">
              Currently Matched Products
              {selectedMerchant.locations.length > 1 && " at "}
              {selectedMerchant.locations.length > 1 && (
                <div>
                  <select
                    value={previewLocation}
                    id={"previewLocation"}
                    className="form-select"
                    onChange={(e) => {
                      setPreviewLocation(e.target.value);
                    }}
                  >
                    {selectedMerchant.locations.map((location) => (
                      <option key={location.store_id} value={location.store_id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </h2>
          <Index
            indexName={`${config.environment}_${selectedMerchant?.easy_id}_products`}
            indexId="result"
          >
            <Configure
              hitsPerPage={15}
              filters={
                onlyDiscountedItems
                  ? `${previewLocation}_discounted_price:>0`
                  : `${previewLocation}_qty:>0`
              }
            />
            <Preview storeId={previewLocation} />
          </Index>
        </div>
      </div>
    </InstantSearch>
  );
};
export default Categories;
